import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from '../hooks';
import { useTranslation } from 'react-i18next';
import { mapRecordToSelectOption } from '@app/helpers';
import * as yup from 'yup';
import { Kato, Measure, MeasureLabelsFull, Vehicle, Oked } from '@app/models';
import {
  KatoSelectTrigger,
  OkedSelectTrigger,
  SelectOption,
} from '@app/components';

// shadcn/ui imports
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface VehicleCreateFormProps {
  defaultExpanded: boolean;
  onSubmit: (data: VehicleFormData) => void;
  vehicle?: Vehicle;
  setValues?: (values: Partial<VehicleFormValues>) => void;
  edit?: boolean;
  errors?: any;
}

export interface VehicleFormData {
  name: string;
  description: string;
  price: string;
  files: File[];
  kato: string;
  dateOfIssue: string;
  type: string;
  brand: string;
  measure: SelectOption<Measure> | null;
  oked: string | null;
}

export interface VehicleFormValues
  extends Omit<VehicleFormData, 'kato' | 'oked'> {
  kato: Kato | null;
  oked: Oked | null;
}

function VehicleCreateForm(props: VehicleCreateFormProps) {
  const {
    defaultExpanded,
    onSubmit,
    setValues: setValuesProps,
    vehicle,
    edit = false,
    errors: errorsProps,
  } = props;
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);
  const { values, onChange, errors, validate, setValues, setErrors } =
    useForm<VehicleFormValues>({
      name: '',
      description: '',
      price: '',
      dateOfIssue: '',
      files: [],
      kato: null,
      type: '',
      brand: '',
      measure: null,
      oked: null,
    });

  useEffect(() => {
    if (vehicle) {
      setValues({
        oked: vehicle.oked,
        name: vehicle.name,
        description: vehicle.description,
        price: vehicle.price,
        dateOfIssue: vehicle.dateOfIssue,
        kato: vehicle.kato,
        type: vehicle.type,
        brand: vehicle.brand,
        measure: vehicle.measure
          ? {
              label: MeasureLabelsFull[vehicle.measure],
              value: +Measure[vehicle.measure],
            }
          : null,
      });
    }
  }, []);

  useEffect(() => {
    if (setValuesProps) {
      setValuesProps({
        name: values.name,
        description: values.description,
        price: values.price,
        dateOfIssue: values.dateOfIssue,
        type: values.type,
        brand: values.brand,
        kato: values.kato,
        measure: values.measure,
        oked: values.oked,
      });
    }
  }, [values]);

  useEffect(() => {
    if (errorsProps) {
      setErrors(errorsProps);
    }
  }, [errorsProps]);

  const onChangeSwitch = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  const onChangeKato = useCallback(
    (data: SelectOption<Kato>) => {
      onChange(data.value, 'kato');
    },
    [onChange]
  );

  const onSubmitForm = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const hasErrors = await validate(
        yup.object().shape({
          description: yup
            .string()
            .required(t('required', { ns: 'validation' })!),
          brand: yup.string().required(t('required', { ns: 'validation' })!),
          type: yup.string().required(t('required', { ns: 'validation' })!),
          price: yup.string().required(t('required', { ns: 'validation' })!),
          kato: yup
            .object()
            .nullable()
            .required(t('required', { ns: 'validation' })!),
          dateOfIssue: yup
            .string()
            .required(t('required', { ns: 'validation' })!),
          oked: yup
            .object()
            .nullable()
            .required(t('required', { ns: 'validation' })!),
        })
      );

      if (hasErrors) {
        return;
      }

      onSubmit({
        ...values,
        oked: values.oked ? values.oked.code : null,
        name: '',
        kato: values.kato!.code,
        measure: values.measure && values.measure,
      });

      setExpanded(false);
    },
    [t, onSubmit, validate, values]
  );

  const measureOptions = useMemo(() => {
    const measureArray = Object.values(Measure).filter((value) =>
      Number.isFinite(value)
    );
    return measureArray
      .map((measure) => mapRecordToSelectOption(measure, MeasureLabelsFull))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  const onChangeOked = useCallback(
    (data: SelectOption<Oked>) => {
      onChange(data.value, 'oked');
    },
    [onChange]
  );

  const handleMeasureChange = useCallback(
    (value: string) => {
      const selectedOption = measureOptions.find(
        (option) => option.value.toString() === value
      );
      onChange(selectedOption || null, 'measure');
    },
    [measureOptions, onChange]
  );

  const handleInputChange = useCallback(
    (name: string) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onChange(e.target.value, name);
      },
    [onChange]
  );

  if (!edit && !expanded) {
    return (
      <Card className="w-full">
        <CardHeader
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={onChangeSwitch}
        >
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-medium">
                Сдать в аренду
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground mt-1">
                Заполните данные для аренды транспорта
              </CardDescription>
            </div>
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          </div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      {!edit && (
        <CardHeader
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={onChangeSwitch}
        >
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-medium">
                Сдать в аренду
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground mt-1">
                Заполните данные для аренды транспорта
              </CardDescription>
            </div>
            <ChevronUp className="h-5 w-5 text-muted-foreground" />
          </div>
        </CardHeader>
      )}

      <CardContent className={edit ? 'p-6' : 'pt-0 px-6 pb-6'}>
        <form onSubmit={onSubmitForm} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="type" className="text-sm font-medium">
                Тип <span className="text-destructive">*</span>
              </Label>
              <Input
                id="type"
                name="type"
                value={values.type}
                onChange={handleInputChange('type')}
                placeholder="Тип транспорта"
                className={errors.type ? 'border-destructive' : ''}
              />
              {errors.type && (
                <p className="text-sm text-destructive">{errors.type}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="brand" className="text-sm font-medium">
                Марка <span className="text-destructive">*</span>
              </Label>
              <Input
                id="brand"
                name="brand"
                value={values.brand}
                onChange={handleInputChange('brand')}
                placeholder="Марка транспорта"
                className={errors.brand ? 'border-destructive' : ''}
              />
              {errors.brand && (
                <p className="text-sm text-destructive">{errors.brand}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Выберите категорию <span className="text-destructive">*</span>
              </Label>
              <OkedSelectTrigger
                value={values.oked ? values.oked.nameRu : ''}
                onChange={onChangeOked}
                helperText={errors.oked || undefined}
                error={!!errors.oked}
                parentNumber={80000001}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dateOfIssue" className="text-sm font-medium">
                Год выпуска <span className="text-destructive">*</span>
              </Label>
              <Input
                id="dateOfIssue"
                name="dateOfIssue"
                value={values.dateOfIssue}
                onChange={handleInputChange('dateOfIssue')}
                placeholder="Выберите год"
                maxLength={4}
                className={errors.dateOfIssue ? 'border-destructive' : ''}
              />
              {errors.dateOfIssue && (
                <p className="text-sm text-destructive">{errors.dateOfIssue}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="price" className="text-sm font-medium">
                Цена (₸) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="price"
                name="price"
                value={values.price}
                onChange={handleInputChange('price')}
                placeholder="Введите цену"
                className={errors.price ? 'border-destructive' : ''}
              />
              {errors.price && (
                <p className="text-sm text-destructive">{errors.price}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Единицы измерения <span className="text-destructive">*</span>
              </Label>
              <Select
                value={values.measure?.value?.toString() || ''}
                onValueChange={handleMeasureChange}
              >
                <SelectTrigger
                  className={errors.measure ? 'border-destructive' : ''}
                >
                  <SelectValue placeholder="Выберите ед. из." />
                </SelectTrigger>
                <SelectContent>
                  {measureOptions.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value.toString()}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.measure && (
                <p className="text-sm text-destructive">{errors.measure}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Выберите регион <span className="text-destructive">*</span>
              </Label>
              <KatoSelectTrigger
                value={(values.kato ? values.kato.nameRu : '') || undefined}
                onChange={onChangeKato}
                helperText={errors.kato || undefined}
                error={!!errors.kato}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium">
              Краткое содержание <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="description"
              name="description"
              value={values.description}
              onChange={handleInputChange('description')}
              placeholder="Максимальная длина текста 2100 знаков. Осталось 2100 знаков."
              className={`min-h-[120px] resize-none ${
                errors.description ? 'border-destructive' : ''
              }`}
              maxLength={2100}
            />
            <div className="flex justify-between items-center">
              {errors.description && (
                <p className="text-sm text-destructive">{errors.description}</p>
              )}
              <p className="text-sm text-muted-foreground ml-auto">
                {2100 - values.description.length} знаков осталось
              </p>
            </div>
          </div>

          {!edit && (
            <div className="flex justify-end pt-4">
              <Button type="submit" className="px-8">
                Далее
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}

export default memo(VehicleCreateForm);
